import { say } from "../localization/say.ts";
import type { Group, Row } from "./found.ts";

export type Reachable = {
  id: string;
  title: string | null;
  icon: string | null;
  lastAt: string | null;
  pinnedAt: string | null;
};

export type WhereIAm = {
  projects: { conversations: Reachable[] }[];
  personal: Reachable[];
};

export const A_SUGGESTION_HOLDS = 5;

export function everywhereIn(where: WhereIAm): Reachable[] {
  const seen = new Map<string, Reachable>();
  for (const one of [...where.projects.flatMap((p) => p.conversations), ...where.personal]) {
    if (!seen.has(one.id)) seen.set(one.id, one);
  }
  return [...seen.values()];
}

function when(at: string | null): number {
  const parsed = at ? Date.parse(at) : Number.NaN;
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

export function likeliest(where: WhereIAm, here: string | null): Reachable[] {
  return everywhereIn(where)
    .filter((one) => one.id !== here)
    .sort((a, b) => {
      const pinned = Number(b.pinnedAt !== null) - Number(a.pinnedAt !== null);
      if (pinned !== 0) return pinned;
      if (a.pinnedAt !== null && b.pinnedAt !== null) {
        const by = when(b.pinnedAt) - when(a.pinnedAt);
        if (by !== 0) return by;
      }
      const spoken = when(b.lastAt) - when(a.lastAt);
      if (spoken !== 0) return spoken;

      return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
    })
    .slice(0, A_SUGGESTION_HOLDS);
}

function rowOf(one: Reachable): Row {
  return {
    kind: "conversation",
    key: `conversation:${one.id}`,
    title: one.title?.trim() ? one.title : say("chat.unnamed"),
    icon: "messages-square",
    goes: `/c/${one.id}`,
    at: one.lastAt,
  };
}

export function suggested(where: WhereIAm | null, here: string | null): Group[] {
  if (!where) return [];
  const rows = likeliest(where, here).map(rowOf);
  return rows.length === 0 ? [] : [{ id: "suggested", label: say("find.suggested"), rows }];
}
